import { Label } from '@radix-ui/react-label';
import { Checkbox, Table, Text } from '@radix-ui/themes';
import { EmptyBodyCell } from 'components/common/table/empty-body-cell';
import { CommonTooltip } from 'components/common/tooltip';
import { CheckedContext } from 'contexts/layout/checked.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { IBodyCell } from 'interfaces/tables/body';
import { ICheckCell } from 'interfaces/tables/checking';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import { isNil } from 'lodash';
import { MouseEventHandler, useContext, useMemo } from 'react';

export const CheckboxCell = (props: ICheckCell) => {
  const { id, getChecked, setChecked, setLastCheckedIndex } =
    useContext(CheckedContext);

  const { col, index, onShiftClick, afterCheckOne } = props;

  const _item = props.item as WithID<any>;

  const checked = getChecked(_item._id);

  const checkboxID = `cb-${id}-${index}`;

  const handleClick: MouseEventHandler<HTMLButtonElement | HTMLLabelElement> = (
    e
  ) => {
    if (!_item._id) {
      console.error('checkbox item has no _id value!', _item);
      return;
    }

    if (e.shiftKey && onShiftClick) {
      // Allow the parent table to handle shift-click/bulk selection
      onShiftClick(e);
    } else {
      // Handle single checkbox toggle
      const nextValue = !checked;
      setChecked([_item._id], nextValue);
      afterCheckOne?.(_item._id, nextValue);
    }

    // set the last checked index as a reference for shift-click selection
    setLastCheckedIndex(index);

    // prevent this from triggering select row logic (i.e. row.onClick)
    e.stopPropagation();
  };

  return (
    <Table.Cell
      className={StringHelper.classNames([col.classNameFn?.(_item)])}
      data-testid="col-checkbox"
      data-checked={checked}
      align={col.align ?? 'center'}
    >
      <Label
        htmlFor={checkboxID}
        className="CheckboxWrapperLabel"
        style={{ marginTop: '2px' }}
        onClick={handleClick}
      >
        <Checkbox
          id={checkboxID}
          className="cursor-pointer"
          checked={checked}
          color={col.colorFn?.(_item) ?? col.color}
          onClick={handleClick}
        />
      </Label>
    </Table.Cell>
  );
};

export const BasicCell = (props: IBodyCell) => {
  const { col, item } = props;

  const classes = useMemo(
    () => StringHelper.classNames([col.classNameFn?.(item)]),
    [col.classNameFn, item]
  );

  const content = useMemo(() => {
    try {
      if (col.formatFn) {
        return col.formatFn(item, props.rowIndex) ?? <EmptyBodyCell />;
      }

      const anyValue = item[col.key];

      const type = typeof anyValue;

      if (type === 'number') {
        if (isNil(col.decimals)) {
          // full precision
          return anyValue;
        }

        // format to the specified number of decimals
        return anyValue.toFixed(col.decimals);
      }

      switch (col.dataType) {
        case 'date': {
          const value = anyValue ? parseISO(anyValue) : '';
          if (value) {
            return format(value, 'yyyy-MM-dd', {
              timeZone: LOCAL_TIMEZONE,
            });
          }
          break;
        }

        case 'datetime': {
          const value = anyValue ? parseISO(anyValue) : '';
          if (value) {
            return format(value, 'yyyy-MM-dd @ HH:mm z', {
              timeZone: LOCAL_TIMEZONE,
            });
          }
          break;
        }

        default: {
          break;
        }
      }

      if (isNil(anyValue)) {
        return <EmptyBodyCell />;
      }

      return anyValue;
    } catch (e) {
      console.error(e);
      return <EmptyBodyCell />;
    }
  }, [item, props.rowIndex, col]);

  const tooltip = useMemo(() => col.tooltipFn?.(item), [col.tooltipFn, item]);

  return (
    <Table.Cell
      className={classes}
      data-type={props.col.dataType}
      align={props.col.align}
    >
      <CommonTooltip
        disabled={!tooltip}
        text_md={tooltip}
        trigger={
          <Text color={props.col.colorFn?.(props.item) ?? props.col.color}>
            {content}
          </Text>
        }
      />
    </Table.Cell>
  );
};
